import { ROOM_TYPE_STATUS } from '@ibe/constants';
import { z } from 'zod';

/**
 * The Room Types module's contracts.
 *
 * These schemas are the source of truth for the shapes this module reads and
 * writes. The service already validates; these make the portal's feedback local.
 */

const CODE_PATTERN = /^[A-Z0-9-]{2,40}$/u;
const MAX_NAME = 200;
const MAX_DISPLAY_NAME = 255;
const MAX_DESCRIPTION = 4000;
const MAX_SIZE = 32;
const MAX_BED_TYPE = 40;
const MAX_PMS_ID = 40;
const MAX_OCCUPANCY = 100;
const MAX_ROOMS = 10000;

/** Numeric string that does not accept zero, because a room type must sleep at least one. */
function occupancyField(label: string): z.ZodType<string, string> {
  return z
    .string()
    .trim()
    .min(1, `${label} is required`)
    .regex(/^\d+$/, `${label} must be a whole number`)
    .refine(
      (value) => {
        const parsed = Number(value);
        return Number.isFinite(parsed) && parsed >= 1 && parsed <= MAX_OCCUPANCY;
      },
      `${label} must be between 1 and ${String(MAX_OCCUPANCY)}`,
    );
}

function roomCountField(label: string): z.ZodType<string, string> {
  return z
    .string()
    .trim()
    .regex(/^\d*$/, `${label} must be a whole number`)
    .refine(
      (value) => {
        if (value === '') return true;
        const parsed = Number(value);
        return Number.isFinite(parsed) && parsed >= 0 && parsed <= MAX_ROOMS;
      },
      `${label} must be between 0 and ${String(MAX_ROOMS)}`,
    );
}

function optionalText(max: number, tooLong: string): z.ZodString {
  return z.string().trim().max(max, tooLong);
}

export const ROOM_TYPE_STATUS_OPTIONS: readonly { value: string; label: string }[] = [
  { value: ROOM_TYPE_STATUS.ACTIVE, label: 'Active' },
  { value: ROOM_TYPE_STATUS.INACTIVE, label: 'Inactive' },
  { value: ROOM_TYPE_STATUS.ARCHIVED, label: 'Archived' },
];

const statusValues = ROOM_TYPE_STATUS_OPTIONS.map((option) => option.value) as [
  string,
  ...string[],
];

export const roomTypeRow = z.object({
  id: z.string(),
  code: z.string().optional(),
  name: z.string().optional(),
  maxOccupancy: z.number().optional(),
  status: z.string().optional(),
  totalRooms: z.number().optional(),
  sellableRooms: z.number().optional(),
  version: z.number(),
  createdAt: z.string().optional(),
});

export type RoomTypeRow = z.infer<typeof roomTypeRow>;

export const roomTypeDetail = z.object({
  id: z.string(),
  propertyId: z.string(),
  code: z.string(),
  name: z.string(),
  displayName: z.string().nullable(),
  description: z.string().nullable(),
  maxOccupancy: z.number(),
  sizeInSqm: z.string().nullable(),
  bedType: z.string().nullable(),
  totalNoOfRooms: z.number(),
  pmsRoomTypeId: z.string().nullable(),
  status: z.string(),
  version: z.number(),
  totalRooms: z.number(),
  sellableRooms: z.number(),
});

export type RoomTypeDetail = z.infer<typeof roomTypeDetail>;

export const roomTypeFormInput = z.object({
  propertyId: z.uuid({ message: 'Choose a property' }),
  code: z
    .string()
    .trim()
    .toUpperCase()
    .regex(CODE_PATTERN, 'Two to forty letters, digits or hyphens — for example DLX-KING'),
  name: z
    .string()
    .trim()
    .min(1, 'A room type needs a name')
    .max(MAX_NAME, `Keep the name under ${String(MAX_NAME)} characters`),
  displayName: optionalText(
    MAX_DISPLAY_NAME,
    `Keep the display name under ${String(MAX_DISPLAY_NAME)} characters`,
  ),
  description: optionalText(
    MAX_DESCRIPTION,
    `Keep the description under ${String(MAX_DESCRIPTION)} characters`,
  ),
  maxOccupancy: occupancyField('Maximum occupancy'),
  sizeInSqm: z
    .string()
    .trim()
    .max(MAX_SIZE, `Keep the size under ${String(MAX_SIZE)} characters`)
    .regex(/^\d*(\.\d{1,2})?$/, 'Size must be a number with up to two decimal places'),
  bedType: optionalText(MAX_BED_TYPE, `Keep the bed type under ${String(MAX_BED_TYPE)} characters`),
  totalNoOfRooms: roomCountField('Total number of rooms'),
  pmsRoomTypeId: optionalText(
    MAX_PMS_ID,
    `Keep the PMS room type id under ${String(MAX_PMS_ID)} characters`,
  ),
  status: z.enum(statusValues),
  selectedAmenities: z.record(z.string(), z.boolean()),
  amenityFree: z.record(z.string(), z.boolean()),
});

export type RoomTypeFormInput = z.infer<typeof roomTypeFormInput>;

export const createdRoomType = z.object({ id: z.string() });

export type CreatedRoomType = z.infer<typeof createdRoomType>;

export const roomTypeAmenityOption = z.object({
  amenityId: z.string(),
  name: z.string(),
  isFree: z.boolean(),
  isSelected: z.boolean(),
});

export type RoomTypeAmenityOption = z.infer<typeof roomTypeAmenityOption>;

export const roomTypeAmenitiesResponse = z.object({
  amenities: z.array(roomTypeAmenityOption),
});

export type RoomTypeAmenitiesResponse = z.infer<typeof roomTypeAmenitiesResponse>;

export const roomTypeAmenitySelection = z.object({
  amenityId: z.string(),
  isFree: z.boolean(),
});

export type RoomTypeAmenitySelection = z.infer<typeof roomTypeAmenitySelection>;

export const roomTypeAmenitiesInput = z.object({
  selectedAmenities: z.record(z.string(), z.boolean()),
  amenityFree: z.record(z.string(), z.boolean()),
});

export type RoomTypeAmenitiesFormInput = z.infer<typeof roomTypeAmenitiesInput>;
